// The browser guesses a file's type from its name, never its bytes, so a Word file renamed to
// .pdf passes FilePond's own check. This reads the file's first bytes to confirm the name.

const PDF = 'application/pdf';
const DOC = 'application/msword';
const DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

// Acrobat accepts a PDF header anywhere in the first 1024 bytes, and print workflows put a few
// bytes in front of it.
const HEADER_WINDOW = 1024;

const PDF_SIGNATURE = [0x25, 0x50, 0x44, 0x46, 0x2d]; // %PDF-
const OLE2_SIGNATURE = [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1]; // .doc
const ZIP_SIGNATURE = [0x50, 0x4b, 0x03, 0x04]; // .docx

export const PDF_TYPE_REJECTED_LABEL = 'Not a valid PDF';
export const PDF_OR_WORD_TYPE_REJECTED_LABEL = 'Not a valid PDF or Word file';
export const PDF_TYPE_REJECTED_HINT =
    'This file is not a valid PDF, or its name does not end in .pdf. Please save it as a PDF and upload it again.';
export const PDF_OR_WORD_TYPE_REJECTED_HINT =
    'This file is not a valid PDF or Word document, or its name does not end in .pdf, .doc or .docx. '
    + 'Please save it in one of these formats and upload it again.';

const startsWithAt = (bytes: Uint8Array, signature: number[], offset: number) =>
    signature.every((byte, i) => bytes[offset + i] === byte);

const containsWithin = (bytes: Uint8Array, signature: number[]) => {
    for (let offset = 0; offset + signature.length <= bytes.length; offset++) {
        if (startsWithAt(bytes, signature, offset)) {
            return true;
        }
    }
    return false;
};

// Word opens .doc content saved as .docx and the reverse, and those upload today, so either Word
// signature passes for either Word extension.
const isWordDocument = (bytes: Uint8Array) =>
    startsWithAt(bytes, OLE2_SIGNATURE, 0) || startsWithAt(bytes, ZIP_SIGNATURE, 0);

const SIGNATURE_CHECKS: Record<string, (bytes: Uint8Array) => boolean> = {
    [PDF]: bytes => containsWithin(bytes, PDF_SIGNATURE),
    [DOC]: isWordDocument,
    [DOCX]: isWordDocument,
};

// FileReader rather than Blob.arrayBuffer, which the jsdom used by the tests lacks.
const readHeader = (file: File) => new Promise<Uint8Array>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file.slice(0, HEADER_WINDOW));
});

/**
 * FilePond's fileValidateTypeDetectType hook. Returns the browser's type when the bytes confirm it,
 * and '' when they don't, which FilePond then rejects. The bytes never widen what the name allows.
 */
export const detectScannedFileType = (file: File, type: string): Promise<string> => {
    const matches = SIGNATURE_CHECKS[type];
    if (!matches) {
        return Promise.resolve(type);
    }
    return readHeader(file)
        .then(bytes => matches(bytes) ? type : '')
        .catch(error => {
            // A failed read falls back to the name-only check, so a glitch never rejects a real report.
            // No file name in the log: report names carry client names.
            console.warn('Could not read the file header, keeping the browser type', error);
            return type;
        });
};
